import { backendClient } from '../../api/backend'
import { getDocTypesByCountry } from '../../utils/utils'
import {
  SEARCH_REQUEST,
  SEARCH_SUCCESS,
  SEARCH_FAILURE,
  FETCH_RECENT_SEARCHES,
  FETCH_AUTOCOMPLETE_RESULTS,
  RESET_SEARCH_STORE,
  SET_SEARCH_QUERY,
  SET_SEARCH_LOADING_DOCUMENT,
  SET_SEARCH_DOCUMENT,
  SET_SEARCH_IS_MODAL_MOBILE_VISIBLE,
  SEARCH_CLEAR_DOCUMENT,
  SET_SEARCH_DOCUMENT_SOURCE_URL,
  SET_SEARCH_SELECTED_CITATION,
  SET_SELECTED_DOC_TYPES,
  SET_AVAILABLE_DOC_TYPES,
  SET_FILTERED_CITATIONS,
  SET_IS_LOADING_SEARCH_CITATIONS,
  SET_IS_LOADING_AUTO_COMPLETE_SEARCH,
  UPDATE_FROM_AUTO_SELECT,
  RESET_AVAILABLE_DOC_TYPES,
  CANCEL_SEARCH,
  SET_IS_ABROGE_FILTER,
  SET_DATE_FILTER,
  CLEAR_DATE_FILTER,
  SET_DATE_SORT,
  CLEAR_DATE_SORT,
  FILTERED_SEARCH_REQUEST,
  FILTERED_SEARCH_SUCCESS,
  FILTERED_SEARCH_FAILURE,
  PAGINATE_SEARCH_SUCCESS,
  SET_SEARCH_FILTERS,
  CLEAR_SEARCH_FILTERS,
  SET_FILTER_PANEL_OPEN,
  SAVE_SEARCH_SUCCESS,
  DELETE_SAVED_SEARCH_SUCCESS,
  FETCH_SAVED_SEARCHES_SUCCESS,
  TOGGLE_SEARCH_BOOKMARK_SUCCESS,
  FETCH_SEARCH_BOOKMARKS_SUCCESS,
  CHECK_SEARCH_BOOKMARKS_SUCCESS,
  SET_PRE_SEARCH_FILTERS,
  CLEAR_PRE_SEARCH_FILTERS,
  SET_FILTER_MODAL_OPEN,
  FETCH_FILTER_OPTIONS_REQUEST,
  FETCH_FILTER_OPTIONS_SUCCESS,
  FETCH_FILTER_OPTIONS_FAILURE,
} from './types'

// Create a variable to store the current search controller
let searchController = null;

export const setFilteredCitations = citations => ({
  type: SET_FILTERED_CITATIONS,
  payload: citations
})

export const setSelectedDocTypes = docTypes => ({
  type: SET_SELECTED_DOC_TYPES,
  payload: docTypes
})

export const resetAvailableDocTypes = (country = 'MA') => ({
  type: RESET_AVAILABLE_DOC_TYPES,
  payload: getDocTypesByCountry(country)
})

export const setAvailableDocTypes = docTypes => ({
  type: SET_AVAILABLE_DOC_TYPES,
  payload: docTypes
})

export const setSearchLoadingDocument = isLoading => ({
  type: SET_SEARCH_LOADING_DOCUMENT,
  payload: isLoading
})

export const setSearchDocument = document => ({
  type: SET_SEARCH_DOCUMENT,
  payload: document
})

export const setSearchSelectedCitation = citation => ({
  type: SET_SEARCH_SELECTED_CITATION,
  payload: citation
})

export const setSearchIsModalMobileVisible = isVisible => ({
  type: SET_SEARCH_IS_MODAL_MOBILE_VISIBLE,
  payload: isVisible
})

export const setIsLoadingSearchCitations = value => ({
  type: SET_IS_LOADING_SEARCH_CITATIONS,
  payload: value
})

export const setIsLoadingAutoCompleteSearch = value => ({
  type: SET_IS_LOADING_AUTO_COMPLETE_SEARCH,
  payload: value
})

export const setUpdateFromAutoSelect = value => ({
  type: UPDATE_FROM_AUTO_SELECT,
  payload: value
})

export const setSearchDocumentSourceUrl = url => ({
  type: SET_SEARCH_DOCUMENT_SOURCE_URL,
  payload: url
})

export const clearSearchDocument = () => ({
  type: SEARCH_CLEAR_DOCUMENT
})

export const resetSearchStore = () => ({ type: RESET_SEARCH_STORE })
export const searchRequest = () => ({ type: SEARCH_REQUEST })
export const searchSuccess = documents => ({
  type: SEARCH_SUCCESS,
  payload: documents
})
export const searchFailure = error => ({
  type: SEARCH_FAILURE,
  payload: error
})

export const cancelSearch = () => {
  // If there's an active search controller, abort it
  if (searchController) {
    searchController.abort()
    searchController = null
  }
  
  return { type: CANCEL_SEARCH }
}

export const fetchRecentSearchesSuccess = searches => ({
  type: FETCH_RECENT_SEARCHES,
  payload: searches
})

export const fetchAutocompleteResultsSuccess = results => ({
  type: FETCH_AUTOCOMPLETE_RESULTS,
  payload: results
})

export const searchCitations = (query, mode, isAbrogeFilter, searchLanguage) => async (dispatch, getState) => {
  dispatch(searchRequest())

  try {
    // Create a new AbortController for this search request
    if (searchController) {
      searchController.abort() // Abort any existing search
    }
    searchController = new AbortController()
    const signal = searchController.signal

    // Get country preference from Redux state
    const state = getState()
    const country = state.user?.countryPreference || 'MA'

    const response = await backendClient.search(query, mode, signal, country, searchLanguage)
    
    // If the status is 499, it means the request was cancelled by the client
    if (response.status === 499) {
      dispatch({ type: CANCEL_SEARCH })
      searchController = null
      return
    }
    
    // Try to parse response as JSON, handling errors if it's not valid JSON
    let documents
    try {
      documents = await response.json()

    } catch (e) {
      throw new Error('Failed to parse search results')
    }
    
    if (!response.ok) {
      // Handle specific HTTP status codes
      if (response.status === 404) {
        throw new Error('Pas de résultats')
      }
      // Response is not OK, throw an error
      throw new Error(
        documents.detail || `Failed to search: HTTP status ${response.status}`
      )
    }

    dispatch(searchSuccess(documents))
    searchController = null // Clear the controller after successful completion
  } catch (error) {
    // Don't dispatch failure for aborted requests
    if (error.name === 'AbortError') {
      dispatch(setIsLoadingSearchCitations(false))
    } else {
      dispatch(searchFailure(error.message))
    }
    searchController = null // Clear the controller after failure
  }
}

export const setSearchQuery = searchQuery => ({
  type: SET_SEARCH_QUERY,
  payload: searchQuery
})

export const fetchRecentSearches =
  (userId = null, offset = 0) =>
  async dispatch => {
    try {
      const response = await backendClient.getRecentSearches(userId, offset)
      const searches = await response.json()
      dispatch(fetchRecentSearchesSuccess(searches))
      return searches // Return searches to determine if more results are available
    } catch (error) {
      console.error('Error fetching recent searches:', error)
      return [] // Return empty array to prevent undefined errors
    }
  }

export const fetchAutocompleteResults = query => async dispatch => {
  try {
    const response = await backendClient.getAutocompleteResults(query)
    const results = await response.json()
    dispatch(fetchAutocompleteResultsSuccess(results))
  } catch (error) {
    console.error('Error fetching autocomplete results:', error)
    dispatch(fetchAutocompleteResultsSuccess([]))
  }
}
export const setIsAbrogeFilter = value => ({
  type: SET_IS_ABROGE_FILTER,
  payload: value
})

export const setDateFilter = (dateFilter) => ({
  type: SET_DATE_FILTER,
  payload: dateFilter
})

export const clearDateFilter = () => {
  console.log('[DEBUG] clearDateFilter action dispatched');
  return {
    type: CLEAR_DATE_FILTER
  }
}

export const setDateSort = (dateSort) => ({
  type: SET_DATE_SORT,
  payload: dateSort
})

export const clearDateSort = () => {
  console.log('[DEBUG] clearDateSort action dispatched');
  return {
    type: CLEAR_DATE_SORT
  }
}

// ─── Server-Side Filtered Search ─────────────────────────────────────────────

export const setSearchFilters = (filters) => ({
  type: SET_SEARCH_FILTERS,
  payload: filters,
})

export const clearSearchFilters = () => ({
  type: CLEAR_SEARCH_FILTERS,
})

export const setFilterPanelOpen = (isOpen) => ({
  type: SET_FILTER_PANEL_OPEN,
  payload: isOpen,
})

// New search — calls Azure Search once, caches in Redis, returns page 1
// Note: searchLanguage parameter removed - backend queries all languages automatically
export const filteredSearchCitations = (query, mode, filters = null, page = 1) =>
  async (dispatch, getState) => {
    dispatch({ type: FILTERED_SEARCH_REQUEST })

    try {
      if (searchController) {
        searchController.abort()
      }
      searchController = new AbortController()
      const signal = searchController.signal

      const state = getState()
      const countryPref = state.user?.countryPreference
      const countries = Array.isArray(countryPref) ? countryPref : countryPref ? [countryPref] : ['MA']

      // Include pre-search filters if any are set
      const preSearchFilters = state.search?.preSearchFilters || {}
      const hasPreFilters = Object.values(preSearchFilters).some(
        mf => mf && Object.values(mf).some(v =>
          Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined
        )
      )
      const preFilters = hasPreFilters ? { mode_filters: preSearchFilters } : null

      // Build jurisdiction codes from user preferences
      const jurisdictionPref = state.user?.jurisdictionPreference || {}
      const jurisdictionCodes = []
      for (const country of countries) {
        const pref = jurisdictionPref[country]
        if (pref !== undefined && pref !== null && Array.isArray(pref)) {
          jurisdictionCodes.push(...pref)
        }
        // null/absent = all => don't add anything (no filter)
      }

      const dateFilter = state.search?.dateFilter
      let effectiveFilters = filters ? { ...filters } : null
      if (dateFilter?.startDate || dateFilter?.endDate) {
        effectiveFilters = effectiveFilters || {}
        if (dateFilter.startDate) effectiveFilters.date_range_start = dateFilter.startDate
        if (dateFilter.endDate) effectiveFilters.date_range_end = dateFilter.endDate
      }

      const data = await backendClient.filteredSearch(
        query, mode, signal, countries, effectiveFilters, page, 15, preFilters,
        jurisdictionCodes.length > 0 ? jurisdictionCodes : null
      )

      dispatch({
        type: FILTERED_SEARCH_SUCCESS,
        payload: data,
      })
      searchController = null
    } catch (error) {
      if (error.name === 'AbortError') {
        dispatch(setIsLoadingSearchCitations(false))
      } else {
        dispatch({ type: FILTERED_SEARCH_FAILURE, payload: error.message })
      }
      searchController = null
    }
  }

// Page change or filter change — reads from Redis cache, no Azure call
export const paginateSearch = (searchId, filters = null, page = 1) =>
  async (dispatch) => {
    try {
      const data = await backendClient.paginateSearch(searchId, filters, page)
      dispatch({
        type: PAGINATE_SEARCH_SUCCESS,
        payload: data,
      })
    } catch (error) {
      if (error.status === 410) {
        // Cache expired — frontend should trigger a new search
        dispatch({ type: FILTERED_SEARCH_FAILURE, payload: 'Search expired. Please search again.' })
      } else {
        dispatch({ type: FILTERED_SEARCH_FAILURE, payload: error.message })
      }
    }
  }

// ─── Save Search ─────────────────────────────────────────────────────────────

export const saveSearch = (data) => async (dispatch) => {
  try {
    const result = await backendClient.saveSearch(data)
    dispatch({ type: SAVE_SEARCH_SUCCESS, payload: result })
    return result
  } catch (error) {
    console.error('Error saving search:', error)
  }
}

export const fetchSavedSearches = () => async (dispatch) => {
  try {
    const results = await backendClient.getSavedSearches()
    dispatch({ type: FETCH_SAVED_SEARCHES_SUCCESS, payload: results })
  } catch (error) {
    console.error('Error fetching saved searches:', error)
  }
}

export const deleteSavedSearch = (id) => async (dispatch) => {
  try {
    await backendClient.deleteSavedSearch(id)
    dispatch({ type: DELETE_SAVED_SEARCH_SUCCESS, payload: id })
  } catch (error) {
    console.error('Error deleting saved search:', error)
  }
}

// ─── Search Bookmarks ────────────────────────────────────────────────────────

export const toggleSearchBookmark = (documentExternalId, documentType, nodeData = null) =>
  async (dispatch) => {
    try {
      const result = await backendClient.toggleSearchBookmark(documentExternalId, documentType, nodeData)
      dispatch({ type: TOGGLE_SEARCH_BOOKMARK_SUCCESS, payload: result })
      return result
    } catch (error) {
      console.error('Error toggling bookmark:', error)
    }
  }

export const fetchSearchBookmarks = () => async (dispatch) => {
  try {
    const results = await backendClient.getSearchBookmarks()
    dispatch({ type: FETCH_SEARCH_BOOKMARKS_SUCCESS, payload: results })
  } catch (error) {
    console.error('Error fetching bookmarks:', error)
  }
}

export const checkSearchBookmarks = (documentExternalIds) => async (dispatch) => {
  try {
    const result = await backendClient.checkSearchBookmarks(documentExternalIds)
    dispatch({ type: CHECK_SEARCH_BOOKMARKS_SUCCESS, payload: result.bookmarked_ids })
  } catch (error) {
    console.error('Error checking bookmarks:', error)
  }
}

// ─── Pre-Search Filters ────────────────────────────────────────────────────

export const setPreSearchFilters = (filters) => ({
  type: SET_PRE_SEARCH_FILTERS,
  payload: filters,
})

export const clearPreSearchFilters = () => ({
  type: CLEAR_PRE_SEARCH_FILTERS,
})

export const setFilterModalOpen = (isOpen) => ({
  type: SET_FILTER_MODAL_OPEN,
  payload: isOpen,
})

export const fetchFilterOptions = (mode, countryCode) => async (dispatch) => {
  dispatch({ type: FETCH_FILTER_OPTIONS_REQUEST })
  try {
    const data = await backendClient.getFilterOptions(mode, countryCode)
    dispatch({ type: FETCH_FILTER_OPTIONS_SUCCESS, payload: data })
  } catch (error) {
    console.error('Error fetching filter options:', error)
    dispatch({ type: FETCH_FILTER_OPTIONS_FAILURE, payload: error.message })
  }
}
